/* THE COUNTER — cart behaviour for the drawer (Counter 70) and /cart (Counter 75).
   Prestige's elements (cart-drawer, line-item-quantity, quantity-selector, cart-discount-field,
   cart-note) still own every cart mutation. This file adds what Prestige has no part for:
   recently viewed, one-tap add from the empty bag, the Apply button, the returning message,
   the Rebuy empty check — and the drawer's own refresh.

   REFRESH HISTORY (Sept 26 2026) — read before changing:
   1. Prestige opens our drawer after an add but can leave the old contents.
   2. `?section_id=` did not render the PREVIEW theme; a reload fallback closed the drawer.
   3. Prestige's cart event names are unknown, so listening by name caught nothing.
   4. Swapping in place and then announcing `cart:change` still showed stale contents: the
      announcement most likely set off Prestige's own re-render over ours. No announcement.
   So: we watch the REQUESTS (any add/change/update/clear, by anyone) and re-render through
   the cart API with `sections` (theme-aware; cart + drawer HTML in one response). After our
   own add we VERIFY twice that the drawer shows the cart's count; if something reverted it,
   we render again, and as the last resort reload with the drawer reopening itself.
   5. (evening) Once rebuy.js loaded on every page, a remove was SEEN but never re-rendered:
      (a) a mutation re-renders whatever the observed response says; (b) every render has a 6s
      timeout; (c) Rebuy's own `rebuy:cart.change` also triggers a render, ignored for 2s after
      our own render so the two can't loop.
   6. (later) Renders were then SKIPPED — "no section id": Prestige MOVES the open drawer out
      of its .shopify-section. The section id is now CACHED at load, with a fallback lookup of
      the overlay-group section by name.
   7. (later) The drawer then showed items in the EMPTY layout — a tall gap under the header and
      Subtotal/Checkout pushed off-screen. Prestige adds `drawer--center-body` to the drawer when
      the bag is empty and never takes it off after our swap. syncEmpty now strips it whenever
      the bag has items, and watches the drawer's class so a late re-add is stripped too.
   8. (13 Oct 2026) The announcement bar's shipping slide is Liquid-rendered once per page, so it
      stayed "$25 away" after the bag reached $50. Every render now also asks for the announcement
      section (`.shopify-section--announcement-bar`) in the same `sections` request and swaps the
      text of its `[data-counter-shipping]` slide. Only the slide's contents change, so Prestige's
      carousel keeps running.
   9. (13 Oct 2026) First add from an EMPTY bag left Subtotal/Checkout invisible: Prestige sets the
      shadow footer to opacity 0 and fades it in (theme.js ~4498); our swap interrupts the fade, so
      it stayed at 0 with blank space where it should be. syncEmpty now clears the footer's inline
      opacity/transform/hidden whenever the bag has items (again after 400ms, after Prestige's fade).

   ONE CODE FIELD (24 Oct 2026) — discount codes AND gift cards, like Shopify's checkout field.
   A customer's gift card was refused in the drawer: Prestige's <cart-discount-field> sends every code
   to /cart/update.js as `discount`, which only knows discount codes. Now <counter-code> owns Apply:
   (1) try the code as a discount (cart.discount_codes says whether it applied; a refused code is
   taken back off); (2) if refused, try it as a gift card through the Storefront API
   (cartGiftCardCodesAdd on the same cart, so it carries to checkout); (3) only then the error.
   Prestige never sees the typed code: a document-level capture listener stops its change/Enter.
   Applied gift cards are NOT in Liquid or /cart.js, so <counter-giftcards> reads them from the
   Storefront API on each render and draws the rows (× = cartGiftCardCodesRemove): "$X available" is the
   card's balance, the amount is what it takes off THIS bag (min of balance and what is left).
   TOTAL DUE (24 Oct 2026, Ketan): with a gift card on, each Subtotal row in the same form (drawer footer,
   /cart summary, /cart phone pin) drops to a plain row (.cc-subtotal--plain) and a "Total due" row is
   inserted under it in the big figure; Checkout buttons read "Checkout $43.00", or just "Checkout" at
   $0.00. Points are untouched: a gift card is a payment, not a discount. Mockup: Counter 70 · Cart
   Drawer · Gift Card Total.
   SF_TOKEN is the PUBLIC token of the CA Headless channel storefront "Counter theme — cart codes"
   (made for browser code; rotate it there). The US store needs its own.

   RECENTLY VIEWED stores { h: handle, n: name, d: descriptor }, written on the product page
   from vasanti.product_name / vasanti.identity_name (counter-name-split). Those fields are the
   ONLY source of the split — no inference here. Entry without a name → the full title.
   STORAGE KEY IS VERSIONED (`counter:recent:v2`, Sept 26 2026). Bump it whenever the entry
   shape or the naming rule changes.

   Debug: counterCartDebug() in the console. */
(() => {
  if (window.__counterCart) return;
  window.__counterCart = true;

  const KEY = 'counter:recent:v2';
  const REOPEN = 'counter:reopen-drawer';
  const MAX = 8;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const OURS = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Counter-Cart': '1' };
  const MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  const log = [];
  const note = m => { log.push(new Date().toISOString().slice(11, 23) + ' ' + m); if (log.length > 40) log.shift(); };
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]').filter(e => e && typeof e === 'object' && e.h);
    } catch (e) { return []; }
  };
  const wait = ms => new Promise(r => setTimeout(r, ms));
  try { localStorage.removeItem('counter:recent'); } catch (e) {}

  const viewed = document.querySelector('script[data-counter-viewed]');
  if (viewed) {
    try {
      const v = JSON.parse(viewed.textContent);
      if (v.handle) {
        const entry = { h: v.handle, n: v.name || '', d: v.desc || '' };
        localStorage.setItem(KEY, JSON.stringify([entry, ...read().filter(x => x.h !== v.handle)].slice(0, MAX)));
      }
    } catch (e) {}
  }

  const T = (k, f) => (window.CounterStrings && window.CounterStrings[k]) || f;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
  const money = c => {
    const whole = c % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(c / 100);
    } catch (e) { return '$' + (c / 100).toFixed(whole ? 0 : 2); }
  };
  const abs = s => (s && s.startsWith('//') ? 'https:' + s : s);
  const sized = (s, w) => (s ? s + (s.includes('?') ? '&' : '?') + 'width=' + w : '');
  const selectLabel = name => (/colou?r|shade/i.test(name || '') ? T('select_shade', 'Select Shade') : T('select_option', 'Select %option%').replace('%option%', name || 'option'));
  const nameNodes = (n, d) => {
    const a = document.createElement('span'); a.className = 'cc-name'; a.textContent = n;
    if (!d) return [a];
    const b = document.createElement('span'); b.className = 'cc-name__desc'; b.textContent = d;
    return [a, b];
  };

  let cachedSectionId = null;
  const drawerSectionId = () => {
    if (cachedSectionId) return cachedSectionId;
    const d = document.getElementById('cart-drawer');
    const sec = (d && d.closest('.shopify-section')) || document.querySelector('.shopify-section[id$="cart-drawer"]');
    if (sec) cachedSectionId = sec.id.replace('shopify-section-', '');
    return cachedSectionId;
  };
  const drawer = () => {
    const id = drawerSectionId();
    const inSection = id && document.querySelector(`#shopify-section-${CSS.escape(id)} cart-drawer#cart-drawer`);
    return inSection || document.querySelector('cart-drawer#cart-drawer') || document.getElementById('cart-drawer');
  };
  drawerSectionId();
  let cachedBarId;
  const barSectionId = () => {
    if (cachedBarId !== undefined) return cachedBarId;
    const sec = document.querySelector('.shopify-section--announcement-bar');
    cachedBarId = sec && sec.querySelector('[data-counter-shipping]') ? sec.id.replace('shopify-section-', '') : null;
    return cachedBarId;
  };
  barSectionId();
  const swapBar = html => {
    const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-counter-shipping]');
    const cur = document.querySelector('.shopify-section--announcement-bar [data-counter-shipping]');
    if (!next || !cur) return;
    if (cur.innerHTML !== next.innerHTML) { cur.innerHTML = next.innerHTML; note('bar: ' + cur.textContent.trim()); }
  };
  const syncEmpty = () => {
    const d = drawer();
    if (!d) return;
    const empty = !!d.querySelector('[data-cc-empty]');
    if (d.classList.contains('is-empty') !== empty) d.classList.toggle('is-empty', empty);
    if (!empty && d.classList.contains('drawer--center-body')) d.classList.remove('drawer--center-body');
    if (!empty) showFooter(d);
  };
  const showFooter = d => {
    const f = d.shadowRoot && d.shadowRoot.querySelector('[part="footer"]');
    if (!f) return;
    const fix = () => {
      if (f.style.opacity !== '' && f.style.opacity !== '1') { f.style.opacity = ''; note('footer opacity reset'); }
      if (f.style.transform && f.style.transform !== 'none') f.style.transform = '';
      if (f.hidden && d.querySelector('[slot="footer"]')) f.hidden = false;
    };
    fix();
    setTimeout(fix, 400);
  };
  const swapDrawer = html => {
    const next = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-drawer');
    const d = drawer();
    if (!next || !d) { note('swap: missing ' + (!next ? 'html drawer' : 'page drawer')); return false; }
    d.replaceChildren(...Array.from(next.childNodes));
    syncEmpty();
    note('swap: count ' + shownCount());
    return true;
  };
  const shownCount = () => { const d = drawer(); const h = d && d.querySelector('[data-cc-count]'); return h ? +h.dataset.ccCount : -1; };
  const withSections = body => {
    const id = drawerSectionId();
    const sections = [id, barSectionId()].filter(Boolean);
    return { id, body: JSON.stringify(Object.assign({}, body, sections.length ? { sections, sections_url: location.pathname } : {})) };
  };
  const applySections = (data, id) => {
    const bar = barSectionId();
    if (bar && data && data.sections) swapBar(data.sections[bar]);
    const html = id && data && data.sections && data.sections[id];
    if (!html) note('no section html in response');
    return html ? swapDrawer(html) : false;
  };

  let rendering = null;
  let again = false;
  let lastOwnRender = 0;
  const renderFromCart = () => {
    if (rendering) { again = true; return rendering; }
    const { id, body } = withSections({});
    if (!id || !drawer()) { note('render skipped: ' + (!id ? 'no section id' : 'no drawer')); return Promise.resolve(null); }
    lastOwnRender = Date.now();
    const timeout = wait(6000).then(() => { throw new Error('timeout'); });
    rendering = Promise.race([nativeFetch(`${root}cart/update.js`, { method: 'POST', headers: OURS, body }), timeout])
      .then(r => (r && r.ok ? r.json() : null))
      .then(cart => { if (cart) applySections(cart, id); else note('render: no cart'); return cart; })
      .catch(e => { note('render failed ' + (e && e.message)); return null; })
      .finally(() => {
        rendering = null;
        lastOwnRender = Date.now();
        if (again) { again = false; renderFromCart(); }
      });
    return rendering;
  };

  const reopenAfterReload = () => {
    try { sessionStorage.setItem(REOPEN, '1'); } catch (e) {}
    location.reload();
  };
  const openDrawer = async () => {
    await customElements.whenDefined('cart-drawer').catch(() => {});
    const d = drawer();
    if (d && typeof d.show === 'function') { try { d.show(); return; } catch (e) {} }
    const opener = document.querySelector('[aria-controls="cart-drawer"]');
    if (opener) opener.click();
  };
  try {
    if (sessionStorage.getItem(REOPEN)) { sessionStorage.removeItem(REOPEN); setTimeout(openDrawer, 300); }
  } catch (e) {}

  const ensure = async expected => {
    for (const delay of [0, 350]) {
      await wait(delay);
      if (shownCount() === expected) continue;
      note('mismatch shown ' + shownCount() + ' vs cart ' + expected + ' — re-render');
      await renderFromCart();
    }
    await wait(350);
    if (shownCount() !== expected) { note('still stale — reload + reopen'); reopenAfterReload(); }
  };

  const headerIsOurs = h => !!h && (typeof h.get === 'function' ? h.get('X-Counter-Cart') : h['X-Counter-Cart']);
  let mutTimer;
  const afterMutation = () => {
    clearTimeout(mutTimer);
    mutTimer = setTimeout(async () => { const c = await renderFromCart(); if (c) ensure(c.item_count); }, 150);
  };
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = nativeFetch(input, init);
    const ours = headerIsOurs(init && init.headers) || (input && input.headers && headerIsOurs(input.headers));
    if (!ours && MUTATION.test(url)) {
      note('saw ' + url.replace(location.origin, ''));
      p.then(r => { note('status ' + (r && r.status)); afterMutation(); }, () => { note('request failed'); afterMutation(); });
    }
    return p;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (MUTATION.test(String(url))) { note('saw xhr ' + url); this.addEventListener('loadend', () => afterMutation()); }
    return xhrOpen.apply(this, arguments);
  };
  document.addEventListener('rebuy:cart.change', () => {
    if (Date.now() - lastOwnRender < 2000 || rendering) return;
    note('rebuy cart.change');
    afterMutation();
  });

  const SF_TOKEN = '8966dd8c53e5f24bcb3405f039b706df';
  const SF_URL = '/api/2026-10/graphql.json';
  const sf = async (query, variables) => {
    const r = await nativeFetch(SF_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Storefront-Access-Token': SF_TOKEN }, body: JSON.stringify({ query, variables }) });
    if (!r.ok) throw new Error('sf ' + r.status);
    const j = await r.json();
    if (j.errors && j.errors.length) throw new Error('sf ' + j.errors[0].message);
    return j.data;
  };
  const getCart = () => nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(r => r.json());
  const cartGid = async () => 'gid://shopify/Cart/' + (await getCart()).token;
  const GC_FIELDS = 'appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } balance { amount currencyCode } }';
  const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  const moneyFull = c => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(c / 100); }
    catch (e) { return '$' + (c / 100).toFixed(2); }
  };
  const tryDiscount = async code => {
    const before = ((await getCart()).discount_codes || []).filter(d => d.applicable).map(d => d.code);
    if (before.some(c => same(c, code))) return { ok: true };
    const { id, body } = withSections({ discount: [...before, code].join(',') });
    const r = await nativeFetch(`${root}cart/update.js`, { method: 'POST', headers: OURS, body });
    const cart = r.ok ? await r.json() : null;
    const hit = cart && (cart.discount_codes || []).find(d => same(d.code, code));
    if (hit && hit.applicable) return { ok: true, cart, id };
    if (hit) await nativeFetch(`${root}cart/update.js`, { method: 'POST', headers: OURS, body: JSON.stringify({ discount: before.join(',') }) });
    return { ok: false };
  };
  const tryGiftCard = async code => {
    const d = await sf(`mutation($id: ID!, $codes: [String!]!) { cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $codes) { cart { ${GC_FIELDS} } userErrors { code message } } }`, { id: await cartGid(), codes: [code] });
    const p = d && d.cartGiftCardCodesAdd;
    const ok = !!(p && p.cart && p.cart.appliedGiftCards.some(g => same(g.lastCharacters, code.slice(-4))));
    if (!ok) note('gift card refused ' + JSON.stringify(p && p.userErrors));
    return ok;
  };
  const afterCode = async (el, res) => {
    if (el.closest('.cc-page')) { location.reload(); return; }
    if (res && res.cart && res.id) applySections(res.cart, res.id);
    else await renderFromCart();
  };
  const ownInput = t => !!(t && t.matches && t.matches('counter-code cart-discount-field input:not([type="hidden"])'));
  document.addEventListener('change', e => { if (ownInput(e.target)) e.stopPropagation(); }, true);
  document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' || !ownInput(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    const c = e.target.closest('counter-code');
    if (c) c.apply();
  }, true);

  class CounterRecent extends HTMLElement {
    connectedCallback() { if (!this._done) { this._done = true; this.fill(); } }
    async fill() {
      const entries = read();
      if (!entries.length) return;
      const tpl = this.querySelector('template');
      const list = this.querySelector('[data-recent-list]');
      if (!tpl || !list) return;
      const limit = +this.dataset.limit || 3;
      const got = await Promise.all(entries.slice(0, limit + 3).map(e =>
        nativeFetch(`${root}products/${encodeURIComponent(e.h)}.js`).then(r => (r.ok ? r.json() : null)).then(p => (p ? { p, e } : null)).catch(() => null)));
      const items = got.filter(x => x && x.p.available).slice(0, limit);
      if (!items.length) return;
      list.replaceChildren(...items.map(x => this.build(tpl, x.p, x.e)));
      const label = this.querySelector('p[data-recent-label]');
      if (label && this.dataset.recentLabel) label.textContent = this.dataset.recentLabel;
      this.hidden = false;
    }
    build(tpl, p, entry) {
      const n = tpl.content.firstElementChild.cloneNode(true);
      const nm = entry && entry.n ? { n: entry.n, d: entry.d || '' } : { n: p.title, d: '' };
      n.querySelectorAll('[data-slot-link]').forEach(a => { a.href = p.url; });
      n.querySelectorAll('[data-slot="name"]').forEach(e => { e.replaceChildren(...nameNodes(nm.n, nm.d)); });
      n.querySelectorAll('[data-slot="price"]').forEach(e => { e.textContent = p.price_varies ? T('from', 'From %price%').replace('%price%', money(p.price)) : money(p.price); });
      const frame = n.querySelector('[data-slot-media]');
      const m0 = p.media && p.media[0];
      const ratio = (m0 && (m0.aspect_ratio || (m0.width && m0.height && m0.width / m0.height))) || 1;
      if (frame) {
        frame.classList.toggle('cc-media--45', ratio < 0.9);
        frame.classList.toggle('cc-media--sq', ratio >= 0.9);
      }
      const img = n.querySelector('[data-slot-media] img');
      if (img) {
        const src = abs(p.featured_image || (m0 && m0.src));
        if (src) { img.removeAttribute('srcset'); img.src = sized(src, 400); img.alt = ''; img.hidden = false; } else img.remove();
      }
      const add = n.querySelector('[data-slot="add"]');
      if (add) {
        const buyable = p.variants.filter(v => v.available);
        if (p.variants.length === 1 && buyable.length === 1) add.dataset.ccAdd = buyable[0].id;
        else {
          delete add.dataset.ccAdd;
          const o = p.options && p.options[0];
          add.textContent = selectLabel(o && (o.name || o));
        }
      }
      return n;
    }
  }

  class CounterCode extends HTMLElement {
    connectedCallback() {
      const input = this.input();
      if (!input) return;
      if (!input.placeholder || !input.placeholder.trim()) input.placeholder = this.dataset.placeholder || 'Discount code or gift card';
      input.setAttribute('autocomplete', 'off');
    }
    input() { return this.querySelector('cart-discount-field input:not([type="hidden"])'); }
    show(msg) {
      const b = this.querySelector('cart-discount-banner');
      if (!b) return;
      const p = b.querySelector('.cc-code__error');
      if (p && msg) p.textContent = msg;
      b.hidden = !msg;
    }
    async apply() {
      const input = this.input();
      if (!input || this.busy) return;
      const code = input.value.trim();
      if (!code) { input.focus(); return; }
      const field = this.querySelector('cart-discount-field');
      this.busy = true;
      this.show('');
      if (field) field.setAttribute('aria-busy', 'true');
      note('code entered');
      try {
        const d = await tryDiscount(code);
        if (d.ok) { note('code: discount'); await afterCode(this, d); return; }
        if (await tryGiftCard(code.replace(/[\s-]+/g, ''))) { note('code: gift card'); await afterCode(this, null); return; }
        this.show(this.dataset.error || 'That code isn\'t valid.');
      } catch (e) {
        note('code failed ' + e.message);
        this.show(this.dataset.error || 'That code isn\'t valid.');
      } finally {
        this.busy = false;
        if (field) field.removeAttribute('aria-busy');
      }
    }
  }

  class CounterGiftcards extends HTMLElement {
    connectedCallback() { if (!this._done) { this._done = true; this.fill(); } }
    async fill() {
      const tpl = this.querySelector('template');
      const list = this.querySelector('[data-gc-list]');
      if (!tpl || !list) return;
      let cards, cart;
      try {
        cart = await getCart();
        const d = await sf(`query($id: ID!) { cart(id: $id) { ${GC_FIELDS} } }`, { id: 'gid://shopify/Cart/' + cart.token });
        cards = (d && d.cart && d.cart.appliedGiftCards) || [];
      } catch (e) { note('gift cards read failed ' + e.message); return; }
      if (!cards.length) return;
      const cents = m => (m && m.amount != null ? Math.round(parseFloat(m.amount) * 100) : null);
      let left = cart.total_price;
      list.replaceChildren(...cards.map(g => {
        const n = tpl.content.firstElementChild.cloneNode(true);
        const last4 = g.lastCharacters;
        // Storefront's balance is what is left AFTER this cart (tested 24 Oct: a fully used card read $0),
        // so the card's available value is balance + amountUsed.
        const used = cents(g.amountUsed) || 0;
        const bal = cents(g.balance) != null ? cents(g.balance) + used : null;
        note('gc ' + last4 + ' balance ' + JSON.stringify(g.balance) + ' used ' + JSON.stringify(g.amountUsed));
        const take = Math.min(bal != null ? bal : used, left);
        left -= take;
        n.querySelector('[data-slot="label"]').textContent = (this.dataset.label || 'Gift card ••••%s').replace('%s', last4);
        const av = n.querySelector('[data-slot="available"]');
        if (av) { if (bal != null) av.textContent = (this.dataset.available || '%s available').replace('%s', moneyFull(bal)); else av.remove(); }
        n.querySelector('[data-slot="amount"]').textContent = '−' + moneyFull(take);
        const btn = n.querySelector('button');
        btn.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove gift card ending %s').replace('%s', last4));
        btn.addEventListener('click', () => this.drop(g.id, btn));
        return n;
      }));
      this.due(Math.max(0, left));
    }
    due(cents) {
      const form = this.closest('form');
      if (!form) return;
      form.querySelectorAll('[data-cc-due]').forEach(x => x.remove());
      form.querySelectorAll('.cc-subtotal, .cc-summary__total').forEach(row => {
        const amt = row.querySelector('.cc-subtotal__amount');
        if (!amt) return;
        const due = row.cloneNode(true);
        due.setAttribute('data-cc-due', '');
        due.querySelector('span').textContent = this.dataset.totalLabel || 'Total due';
        due.querySelector('.cc-subtotal__amount').textContent = moneyFull(cents);
        row.classList.add('cc-subtotal--plain');
        row.after(due);
      });
      form.querySelectorAll('button[name="checkout"]').forEach(btn => {
        btn.textContent = cents > 0 ? (this.dataset.checkout || 'Checkout %s').replace('%s', moneyFull(cents)) : (this.dataset.checkoutPlain || 'Checkout');
      });
      note('total due ' + cents);
    }
    async drop(gcId, btn) {
      btn.disabled = true;
      try {
        await sf('mutation($id: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $ids) { userErrors { message } } }', { id: await cartGid(), ids: [gcId] });
        note('gift card removed');
      } catch (e) { note('gift card remove failed ' + e.message); }
      await afterCode(this, null);
    }
  }

  class CounterReturning extends HTMLElement {
    connectedCallback() {
      const r = document.referrer || '';
      if (/\/checkouts?\//.test(r) || /checkout\.shopify\.com|shop\.app|paypal\.com/.test(r)) this.hidden = false;
    }
  }

  class CounterRebuy extends HTMLElement {
    connectedCallback() {
      const m = this.querySelector('[data-rebuy-id]');
      if (!m) return;
      const has = () => !!m.querySelector('a[href*="/products/"], [data-rebuy-product-id], img');
      const settle = () => { if (has()) this.removeAttribute('data-empty'); else this.setAttribute('data-empty', 'true'); };
      setTimeout(settle, 2500);
      setTimeout(settle, 8000);
    }
  }

  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
  define('counter-recent', CounterRecent);
  define('counter-code', CounterCode);
  define('counter-returning', CounterReturning);
  define('counter-rebuy', CounterRebuy);
  define('counter-giftcards', CounterGiftcards);

  syncEmpty();
  if (drawer()) new MutationObserver(syncEmpty).observe(drawer(), { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  window.addEventListener('pageshow', e => { if (e.persisted) renderFromCart(); });

  document.addEventListener('click', async e => {
    const add = e.target.closest('[data-cc-add]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      if (add.getAttribute('aria-busy') === 'true') return;
      add.setAttribute('aria-busy', 'true');
      const onPage = !!add.closest('.cc-page');
      note('add ' + add.dataset.ccAdd);
      try {
        const { id, body } = withSections({ items: [{ id: +add.dataset.ccAdd, quantity: 1 }] });
        const r = await nativeFetch(`${root}cart/add.js`, { method: 'POST', headers: OURS, body });
        if (!r.ok) throw new Error('add ' + r.status);
        if (onPage) { location.reload(); return; }
        applySections(await r.json(), id);
        const cart = await renderFromCart();
        if (cart) await ensure(cart.item_count);
        else reopenAfterReload();
      } catch (err) {
        note('add failed ' + err.message);
        location.href = add.href;
      } finally {
        add.removeAttribute('aria-busy');
      }
      return;
    }
    const apply = e.target.closest('[data-cc-apply]');
    if (apply) {
      e.preventDefault();
      const c = apply.closest('counter-code');
      if (c) c.apply();
    }
  }, true);

  window.counterCartRender = renderFromCart;
  window.counterCartDebug = () => ({
    sectionId: drawerSectionId(),
    barSectionId: barSectionId(),
    drawers: document.querySelectorAll('#cart-drawer').length,
    drawerInSection: !!(drawer() && drawer().closest('.shopify-section')),
    drawerClass: drawer() && drawer().className,
    shown: shownCount(),
    sfToken: !!SF_TOKEN,
    rendering: !!rendering,
    width: drawer() && getComputedStyle(drawer()).getPropertyValue('--cc-drawer-w'),
    recent: read(),
    log: log.slice()
  });
})();
